import { useEffect, useRef, useState } from 'react';

import type { NotificationStreamEvent } from '@/api/notificationStream';
import {
  type NotificationStreamState,
  startNotificationStream,
  useOptionalNotificationStream,
} from '@/context/NotificationStreamContext';

type HookState = 'idle' | 'connecting' | 'connected' | 'unavailable';

interface UseNotificationStreamOptions {
  enabled: boolean;
  onNotification: (event: NotificationStreamEvent) => void;
}

const stateMap: Record<NotificationStreamState, HookState> = {
  idle: 'idle',
  connecting: 'connecting',
  open: 'connected',
  down: 'unavailable',
};

/**
 * Delivers stream events to `onNotification` while `enabled`. Inside a
 * NotificationStreamProvider it reuses the shared connection; on public pages,
 * which have no provider, it opens its own while enabled.
 */
export function useNotificationStream({ enabled, onNotification }: UseNotificationStreamOptions): {
  state: HookState;
  isConnected: boolean;
} {
  const shared = useOptionalNotificationStream();
  const [ownState, setOwnState] = useState<NotificationStreamState>('idle');
  const onNotificationRef = useRef(onNotification);

  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const subscribe = shared?.subscribe;
  useEffect(() => {
    if (!enabled || !subscribe) {
      return;
    }

    return subscribe((event) => onNotificationRef.current(event));
  }, [enabled, subscribe]);

  const hasShared = shared !== null;
  useEffect(() => {
    if (hasShared || !enabled) {
      setOwnState('idle');

      return;
    }

    const connection = startNotificationStream(setOwnState);
    const unsubscribe = connection.subscribe((event) => onNotificationRef.current(event));

    return () => {
      unsubscribe();
      connection.stop();
    };
  }, [enabled, hasShared]);

  const raw = !enabled ? 'idle' : shared ? shared.state : ownState;
  const state = stateMap[raw];

  return { state, isConnected: state === 'connected' };
}
